import { AjaxOptions, XMLHttpRequestProcessed } from "@otto-ec/global-resources";
import { baseUrl } from "../../../../../typescript/config";
import { addCsrfTokenTo } from "../../../../../typescript/utils";
import PasswordMeterElements from "./password_meter_elements";
import PasswordStrengthRuleElement from "./rule_element";

class PasswordStrengthMeter {
    private readonly $documentObject: Document;
    private readonly initialStrength = "INVALID";
    private strength = this.initialStrength;
    private readonly passwordMeterElements: PasswordMeterElements;
    private readonly blockListInputElements: Map<string, HTMLElement>;
    private hasError = false;
    private allPasswordRulesFulfilled: boolean = false;
    private readonly passwordInputField: HTMLInputElement;
    private readonly passwordStrengthControllerUrlPath = baseUrl + "/rest/password-strength/validate";
    private readonly passwordMinLength = 8;
    private readonly invalidCharactersResultType = "INVALID_CHARACTERS";
    private readonly ruleElements: Array<PasswordStrengthRuleElement>;
    private readonly ruleBox: HTMLElement;

    constructor(document: Document, blockListInputSelectors: Map<string, string>) {
        this.$documentObject = document;
        this.blockListInputElements = this.getBlocklistInputElements(blockListInputSelectors);
        this.passwordMeterElements = new PasswordMeterElements(document);
        this.passwordInputField = document.querySelector(".id_js_password_password")! as HTMLInputElement;
        this.ruleElements = this.initRuleElements();
        this.ruleBox = this.$documentObject.querySelector(".password_passwordRules") as HTMLElement;
    }

    private initRuleElements(): Array<PasswordStrengthRuleElement> {
        let result = new Array<PasswordStrengthRuleElement>();
        const ruleHtmlElements = this.$documentObject.querySelectorAll(".password_rule");
        for (let i = 0; ruleHtmlElements[i]; i++) {
            const ruleHtmlElement = ruleHtmlElements[i];
            const ruleLabelOfElement = ruleHtmlElement.getAttribute("data-js-label");
            if (ruleLabelOfElement) {
                result.push(new PasswordStrengthRuleElement(ruleHtmlElement));
            }
        }

        return result;
    }

    registerEventListeners() {
        this.passwordInputField.addEventListener("focusin", () => this.onFocusIn());
        this.passwordInputField.addEventListener("focusout", () => this.onFocusOut());
        this.passwordInputField.addEventListener("input", () => this.updateStatus());

        this.blockListInputElements.forEach((elem) => elem.addEventListener("input", () => this.updateStatus()));
    }

    private onFocusIn() {
        this.hideEmptyErrorIfPasswordFieldIsEmpty();
        this.passwordMeterElements.toggleMeterColorAndLabel(this.strength);
        if (!this.allPasswordRulesFulfilled) {
            this.showPasswordRuleList();
        }
        if (!this.hasError) {
            this.passwordMeterElements.showStrengthMeter();
        }
    }

    private onFocusOut() {
        if (this.passwordInputField.value.length === 0) {
            this.strength = this.initialStrength;
            this.passwordMeterElements.hideStrengthMeter();
            this.passwordMeterElements.resetMeterColorsAndText(this.initialStrength);
            this.hideErrorMessageOnPasswordInputField();
            this.restorePlaceHolderTextIfInputFieldEmpty();
            this.showEmptyError();
            this.hidePasswordRuleList();
        }
    }

    private updateStatus() {
        const passwordFieldLength = this.passwordInputField.value.length;
        if (passwordFieldLength === 0) {
            this.hasError = false;
            this.resetAllRules();
            this.passwordMeterElements.resetMeterColorsAndText(this.strength);
        } else {
            const payload = {
                password: this.passwordInputField.value,
                blockList: this.getBlocklistValues(),
            };
            const ajaxOptions: AjaxOptions = {
                url: this.passwordStrengthControllerUrlPath,
                method: "POST",
                data: JSON.stringify(payload),
                contentType: "application/json; charset=UTF-8",
            };
            addCsrfTokenTo(ajaxOptions);

            const fetchPromise = window.o_util.ajax(ajaxOptions);
            this.sendPasswordStrengthRequest(fetchPromise, passwordFieldLength);
        }
    }

    private getBlocklistInputElements(blockListInputSelectors: Map<string, string>) {
        let result = new Map<string, HTMLElement>();
        blockListInputSelectors.forEach((selector, fieldName) => {
            let inputElem = this.$documentObject.querySelector(selector) as HTMLElement;
            if (inputElem !== null) {
                result.set(fieldName, inputElem);
            }
        });
        return result;
    }

    private getBlocklistValues() {
        let blockListValues = Object.create(null);
        this.blockListInputElements.forEach((elem, fieldName) => {
            const isInputElement = elem instanceof HTMLInputElement;
            if (isInputElement) {
                const inputElement = elem as HTMLInputElement;
                blockListValues[fieldName] = inputElement.value;
            } else {
                blockListValues[fieldName] = elem.innerText;
            }
        });
        return blockListValues;
    }

    private sendPasswordStrengthRequest(fetchPromise: Promise<XMLHttpRequestProcessed>, passwordFieldLength: number) {
        fetchPromise
            .then((response) => {
                if (!response || response.status !== 200 || typeof response.responseJSON !== "string") {
                    console.error("error while parsing the response payload");
                    return;
                }

                const responsePayload = JSON.parse(response.responseJSON as string);
                const errorMessage = responsePayload.specificErrorMessage;
                this.hasError = errorMessage != null;
                this.strength = responsePayload.strength;

                this.updateUi(responsePayload.validatedRules, responsePayload.createPasswordResult, passwordFieldLength, errorMessage);
            })
            .catch(function (exception) {
                console.error("error while talking to backend", exception);
            });
    }

    private updateUi(validatedRules: Record<string, boolean>, createPasswordResultType: string, passwordFieldLength: number, errorMessage: string | null) {
        this.hideErrorMessageFromPageSubmit();
        this.passwordMeterElements.toggleMeterColorAndLabel(this.strength);

        this.highlightValidatedRules(validatedRules);

        if (this.showErrorInInputField(createPasswordResultType, passwordFieldLength)) {
            this.passwordMeterElements.hideStrengthMeter();
            this.showErrorMessageOnPasswordInputField(errorMessage!);
        } else {
            this.passwordMeterElements.showStrengthMeter();
            this.hideErrorMessageOnPasswordInputField();
        }

        this.addOrRemoveErrorStyleFromInputField(createPasswordResultType, passwordFieldLength);
    }

    private resetAllRules() {
        this.ruleElements.forEach((it) => {
            it.unmarkFulfilled();
        });
    }

    private showErrorInInputField(createPasswordResultType: string, passwordFieldLength: number) {
        return this.hasError && (createPasswordResultType == this.invalidCharactersResultType || passwordFieldLength >= this.passwordMinLength);
    }

    private highlightValidatedRules(validatedRules: Record<string, boolean>) {
        let allFulfilled = true;
        for (let i = 0; this.ruleElements[i]; i++) {
            const ruleElement = this.ruleElements[i];
            let isFulfilled = ruleElement.isFulfilled(validatedRules);
            if (isFulfilled) {
                ruleElement.markFulfilled();
            } else {
                ruleElement.unmarkFulfilled();
            }

            allFulfilled = allFulfilled && isFulfilled;
        }
        this.allPasswordRulesFulfilled = allFulfilled;
        this.hideListOfRulesIfAllConditionsFulfilled(allFulfilled);
    }

    private showErrorMessageOnPasswordInputField(message: string) {
        const errorMessageTextElement = this.$documentObject.querySelector(".password_validation_error > small") as HTMLElement;
        errorMessageTextElement.innerHTML = message;

        (errorMessageTextElement.parentElement as HTMLElement).classList.remove("invisible");
    }

    private hideErrorMessageOnPasswordInputField() {
        const wrappingDiv = this.$documentObject.querySelector(".password_validation_error") as HTMLElement;
        wrappingDiv.classList.add("invisible");
    }

    private hideErrorMessageFromPageSubmit() {
        const staticErrorMessage = this.$documentObject.querySelector<HTMLElement>(".p_message--error");
        /*                      */
        staticErrorMessage?.classList.add("invisible");
    }

    private restorePlaceHolderTextIfInputFieldEmpty() {
        const passwordErrorLabel = document.querySelector(".password_validation_error");
        const passwordRepeatErrorLabel = document.querySelector(".js_password_repeatPassword_mismatch");
        /*                      */
        const hasError = [passwordErrorLabel, passwordRepeatErrorLabel].some((label) => !label?.classList.contains("invisible"));

        if (this.passwordInputField.value.length == 0) {
            const countLabelElement = this.$documentObject.querySelector<HTMLElement>(".password_char_counter")!;
            const placeHolderText = this.passwordInputField.getAttribute("placeholder");
            countLabelElement.innerHTML = `${placeHolderText}`;
            if (!hasError) {
                this.showMinimumLengthHint();
            }
        }
    }

    private hideMinimumLengthHint() {
        const hintBox = this.$documentObject.querySelector(".password_char_hint") as Element;
        hintBox.classList.add("invisible");
    }

    private showMinimumLengthHint() {
        const hintBox = this.$documentObject.querySelector(".password_char_hint") as Element;
        hintBox.classList.remove("invisible");
    }

    private hideListOfRulesIfAllConditionsFulfilled(allFulfilled: boolean) {
        if (allFulfilled && !this.hasError) {
            this.hidePasswordRuleList();
        } else {
            this.showPasswordRuleList();
        }
    }

    private showPasswordRuleList() {
        this.ruleBox.classList.remove("invisible");
    }

    private hidePasswordRuleList() {
        this.ruleBox.classList.add("invisible");
    }

    private addOrRemoveErrorStyleFromInputField(createPasswordResultType: string, passwordLength: number) {
        const inputFieldBox = this.$documentObject.querySelector<HTMLElement>(".id_js_password_inputComponent")!;
        const className = "pl_input--invalid";
        if (this.hasError && (createPasswordResultType == this.invalidCharactersResultType || passwordLength >= this.passwordMinLength)) {
            inputFieldBox.classList.add(className);
        } else {
            inputFieldBox.classList.remove(className);
        }
    }

    private showEmptyError() {
        const inputFieldBox = this.$documentObject.querySelector<HTMLElement>(".id_js_password_inputComponent")!;
        const classNameFieldBox = "pl_input--invalid";
        inputFieldBox.classList.add(classNameFieldBox);

        const emptyHintField = this.$documentObject.querySelector<HTMLElement>(`[data-qa="fti-registration-password-field-empty-error-hint"]`)!;
        const classNameEmptyHint = "invisible";
        emptyHintField.classList.remove(classNameEmptyHint);

        this.hideMinimumLengthHint();
    }

    private hideEmptyErrorIfPasswordFieldIsEmpty() {
        if (this.passwordInputField.value.length === 0) {
            const inputFieldBox = this.$documentObject.querySelector<HTMLElement>(".id_js_password_inputComponent")!;
            const classNameFieldBox = "pl_input--invalid";
            inputFieldBox.classList.remove(classNameFieldBox);

            const emptyHintField = this.$documentObject.querySelector<HTMLElement>(`[data-qa="fti-registration-password-field-empty-error-hint"]`)!;
            const classNameHint = "invisible";
            emptyHintField.classList.add(classNameHint);

            this.hideMinimumLengthHint();
        }
    }
}

export type PasswordStrengthResponse =
    | undefined
    | {
          createPasswordResult: string;
          specificErrorMessage: string | null;
          strength: string;
          validatedRules: Record<string, boolean>;
      };

export default PasswordStrengthMeter;
